import React, { useEffect, useMemo, useState } from "react";
import YhButton from "../../ui/YhButton";
import YhSelect from "../../ui/YhSelect";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../ui/YhTable";
import {
  isTruthy,
  openErrorNotification,
} from "../../helpers/methods";
import NewLeaveApplication from "./components/NewLeaveApplication/NewLeaveApplication";
import customErrorHandler from "../../utils/customErrorHandler";
import notifiers from "../../global/constants/NotificationConstants";
import { useAppSelector } from "../../utils/hooks";
import { selectName, selectUserId } from "../../redux/authSlice";
import { getLeave, getUserWiseDetails } from "./LeaveService";
import { store } from "../../utils/store";

type LeaveTab = "balance" | "history" | "upcoming";
type LeaveTypeKey = "total" | "sick" | "casual" | "earned";

type BalanceRow = {
  id: LeaveTypeKey;
  type: string;
  description: string;
  entitled: number;
  used: number;
  available: number;
  pending: number;
  balance: number;
  accent: string;
  accentSoft: string;
  icon: React.ReactNode;
};

const Icon = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`h-[18px] w-[18px] ${className}`}
    aria-hidden
  >
    {children}
  </svg>
);

const CalendarIcon = () => (
  <Icon>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </Icon>
);

const HeartIcon = () => (
  <Icon>
    <path d="M19.5 12.5c1.5-1.6 2-3.2 2-4.7A4.3 4.3 0 0 0 17.2 3.5c-1.5 0-2.8.7-3.7 1.8C12.6 4.2 11.3 3.5 9.8 3.5A4.3 4.3 0 0 0 5.5 7.8c0 1.5.5 3.1 2 4.7L12 20l7.5-7.5Z" />
  </Icon>
);

const UmbrellaIcon = () => (
  <Icon>
    <path d="M12 2a9 9 0 0 0-9 9h18a9 9 0 0 0-9-9Z" />
    <path d="M12 11v8a2 2 0 0 0 4 0" />
  </Icon>
);

const BalanceIcon = () => (
  <Icon>
    <path d="M12 3v18M5 7h14M7 7l-3 7h6L7 7ZM17 7l-3 7h6l-3-7Z" />
  </Icon>
);

const HistoryIcon = () => (
  <Icon>
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5M12 7v5l3 2" />
  </Icon>
);

const UpcomingIcon = () => (
  <Icon>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" />
  </Icon>
);

const ChevronIcon = ({ className = "" }: { className?: string }) => (
  <Icon className={className}>
    <path d="M9 6l6 6-6 6" />
  </Icon>
);

const InfoIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </Icon>
);

const PlusIcon = () => (
  <Icon>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const pctAvailable = (available: number, entitled: number) => {
  if (entitled <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((available / entitled) * 100)));
};

const statusLabel = (status: string) => {
  if (status === "0") return "Pending";
  if (status === "1") return "Approved";
  if (status === "2") return "Rejected";
  return "Pending";
};

const statusClass = (status: string) => {
  if (status === "0") return "bg-[#FFF7ED] text-[#EA580C]";
  if (status === "1") return "bg-[#ECFDF5] text-[#059669]";
  if (status === "2") return "bg-[#FEF2F2] text-[#DC2626]";
  return "bg-[#F3F4F6] text-[#6B7280]";
};

const getInitials = (name: string) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const DaysCell = ({
  value,
  accent,
}: {
  value: number;
  accent?: string;
}) => (
  <div className="leading-tight">
    <div
      className="text-[15px] font-bold"
      style={accent ? { color: accent } : { color: "#111827" }}
    >
      {value}
    </div>
    <div className="text-[11px] font-medium text-[#9CA3AF]">Days</div>
  </div>
);

const Leave = () => {
  const privileges = store.getState().auth?.privileges || {};
  const userIdFromRedux = useAppSelector(selectUserId);
  const userName =
    useAppSelector(selectName) || store.getState().auth.userName || "User";

  const [activeTab, setActiveTab] = useState<LeaveTab>("balance");
  const [isOpenLeaveForm, setOpenLeaveForm] = useState(false);
  const [leaveInfo, setLeaveInfo] = useState<any>({});
  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [calendarYear, setCalendarYear] = useState(
    String(new Date().getFullYear())
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Soft create gate (same idea as old page). Never block viewing the page.
  const leavePriv = privileges?.Leave;
  const canCreate =
    !leavePriv ||
    leavePriv.includes("create") ||
    leavePriv.includes("full control") ||
    leavePriv.includes("view");

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2].map((year) => ({
      label: `Calendar Year ${year}`,
      value: String(year),
    }));
  }, []);

  const loadBalance = async () => {
    try {
      setLoadingBalance(true);
      const response = await getLeave(userIdFromRedux);
      setLeaveInfo(response?.data || {});
    } catch (error: any) {
      if (customErrorHandler({}, error) === false) return;
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoadingBalance(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await getUserWiseDetails(userIdFromRedux);
      const list = Array.isArray(response?.data) ? response.data : [];
      const mapped = list.map((item: any) => {
        const status = String(item.is_status ?? "0");
        return {
          id: item._id,
          _id: item._id,
          createdAt: item.createdAt ? String(item.createdAt).slice(0, 10) : "—",
          leave_type: item.leave_type || "—",
          date_range:
            item.from_date && item.to_date
              ? `${item.from_date} - ${item.to_date}`
              : "—",
          from_date: item.from_date || "",
          to_date: item.to_date || "",
          total_days: item.total_days ?? "—",
          comments: item.comments || "—",
          is_status: status,
        };
      });
      setHistoryRows(mapped);
    } catch (error: any) {
      if (customErrorHandler({}, error) === false) return;
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
      setHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadBalance();
    loadHistory();
  }, [userIdFromRedux]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [activeTab, calendarYear]);

  const pendingByType = useMemo(() => {
    const map: Record<string, number> = {
      total: 0,
      sick: 0,
      casual: 0,
      earned: 0,
    };
    historyRows.forEach((row) => {
      if (row.is_status !== "0") return;
      const days = toNum(row.total_days);
      const type = String(row.leave_type || "").toLowerCase();
      map.total += days;
      if (type.includes("sick")) map.sick += days;
      else if (type.includes("casual")) map.casual += days;
      else if (type.includes("earned")) map.earned += days;
    });
    return map;
  }, [historyRows]);

  const balanceRows: BalanceRow[] = useMemo(() => {
    const totalEntitled = toNum(leaveInfo?.total_leave);
    const totalAvailable = toNum(leaveInfo?.total_leave_available);
    const sickEntitled = toNum(leaveInfo?.sick_leave);
    const sickAvailable = toNum(leaveInfo?.sick_leave_available);
    const casualEntitled = toNum(leaveInfo?.casual_leave);
    const casualAvailable = toNum(leaveInfo?.casual_leave_available);
    const earnedEntitled = toNum(leaveInfo?.earned_leave);
    const earnedAvailable = toNum(leaveInfo?.earned_leave_available);

    const rows: BalanceRow[] = [
      {
        id: "total",
        type: "Total Leaves",
        description: "All types combined",
        entitled: totalEntitled,
        used: Math.max(0, +(totalEntitled - totalAvailable).toFixed(2)),
        available: totalAvailable,
        pending: pendingByType.total,
        balance: totalAvailable,
        accent: "#A02448",
        accentSoft: "#F3E8FF",
        icon: <CalendarIcon />,
      },
      {
        id: "sick",
        type: "Sick Leave",
        description: "For medical reasons",
        entitled: sickEntitled,
        used: Math.max(0, +(sickEntitled - sickAvailable).toFixed(2)),
        available: sickAvailable,
        pending: pendingByType.sick,
        balance: sickAvailable,
        accent: "#2563EB",
        accentSoft: "#DBEAFE",
        icon: <HeartIcon />,
      },
      {
        id: "casual",
        type: "Casual Leave",
        description: "For personal reasons",
        entitled: casualEntitled,
        used: Math.max(0, +(casualEntitled - casualAvailable).toFixed(2)),
        available: casualAvailable,
        pending: pendingByType.casual,
        balance: casualAvailable,
        accent: "#16A34A",
        accentSoft: "#DCFCE7",
        icon: <UmbrellaIcon />,
      },
    ];

    if (earnedEntitled > 0) {
      rows.push({
        id: "earned",
        type: "Earned Leave",
        description: "Accrued leave balance",
        entitled: earnedEntitled,
        used: Math.max(0, +(earnedEntitled - earnedAvailable).toFixed(2)),
        available: earnedAvailable,
        pending: pendingByType.earned,
        balance: earnedAvailable,
        accent: "#D97706",
        accentSoft: "#FEF3C7",
        icon: <CalendarIcon />,
      });
    }

    return rows.filter((r) => r.id === "total" || r.entitled > 0);
  }, [leaveInfo, pendingByType]);

  const summaryCards = balanceRows.filter((r) =>
    ["total", "sick", "casual"].includes(r.id)
  );

  const filteredHistory = useMemo(() => {
    return historyRows.filter((row) => {
      const yearMatch =
        !row.createdAt ||
        row.createdAt === "—" ||
        String(row.createdAt).startsWith(calendarYear);
      return yearMatch;
    });
  }, [historyRows, calendarYear]);

  const upcomingRows = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return filteredHistory.filter((row) => {
      if (!row.from_date) return false;
      const from = new Date(row.from_date);
      if (Number.isNaN(from.getTime())) return false;
      return from >= today && (row.is_status === "0" || row.is_status === "1");
    });
  }, [filteredHistory]);

  const openApplyLeave = () => setOpenLeaveForm(true);

  const handleCloseLeaveForm = () => {
    setOpenLeaveForm(false);
    loadBalance();
    loadHistory();
  };

  const balanceColumns: YhTableColumn[] = useMemo(
    () => [
      {
        field: "type",
        headerName: "LEAVE TYPE",
        flex: 1.4,
        minWidth: 220,
        renderCell: (params: YhTableCellParams) => (
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{
                backgroundColor: params.row.accentSoft,
                color: params.row.accent,
              }}
            >
              {params.row.icon}
            </div>
            <div>
              <div className="text-sm font-semibold text-[#111827]">
                {params.row.type}
              </div>
              <div className="text-xs text-[#9CA3AF]">
                {params.row.description}
              </div>
            </div>
          </div>
        ),
      },
      {
        field: "entitled",
        headerName: "ENTITLED",
        minWidth: 100,
        renderCell: (params) => <DaysCell value={params.row.entitled} />,
      },
      {
        field: "used",
        headerName: "USED",
        minWidth: 90,
        renderCell: (params) => <DaysCell value={params.row.used} />,
      },
      {
        field: "available",
        headerName: "AVAILABLE",
        minWidth: 100,
        renderCell: (params) => <DaysCell value={params.row.available} />,
      },
      {
        field: "pending",
        headerName: "PENDING",
        minWidth: 90,
        renderCell: (params) => <DaysCell value={params.row.pending} />,
      },
      {
        field: "balance",
        headerName: "BALANCE",
        minWidth: 100,
        renderCell: (params) => (
          <DaysCell value={params.row.balance} accent={params.row.accent} />
        ),
      },
      {
        field: "status",
        headerName: "STATUS",
        minWidth: 140,
        renderCell: (params) => {
          const pct = pctAvailable(params.row.available, params.row.entitled);
          return (
            <span className="text-sm font-semibold text-[#10B981]">
              {pct}% Available
            </span>
          );
        },
      },
      {
        field: "action",
        headerName: "",
        width: 48,
        renderCell: () => <ChevronIcon className="text-[#C4C9D4]" />,
      },
    ],
    []
  );

  const historyColumns: YhTableColumn[] = useMemo(
    () => [
      {
        field: "createdAt",
        headerName: "Date",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "leave_type",
        headerName: "Leave Type",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "date_range",
        headerName: "Date Range",
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: "total_days",
        headerName: "Days",
        minWidth: 90,
      },
      {
        field: "comments",
        headerName: "Reason",
        flex: 1.2,
        minWidth: 160,
      },
      {
        field: "is_status",
        headerName: "Status",
        minWidth: 120,
        renderCell: (params) => (
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(
              params.row.is_status
            )}`}
          >
            {statusLabel(params.row.is_status)}
          </span>
        ),
      },
    ],
    []
  );

  const tabs: { key: LeaveTab; label: string; icon: React.ReactNode }[] = [
    { key: "balance", label: "Leave Balance", icon: <BalanceIcon /> },
    { key: "history", label: "Leave History", icon: <HistoryIcon /> },
    { key: "upcoming", label: "Upcoming Leaves", icon: <UpcomingIcon /> },
  ];

  const tableRows =
    activeTab === "balance"
      ? balanceRows
      : activeTab === "history"
        ? filteredHistory
        : upcomingRows;

  const tableColumns =
    activeTab === "balance" ? balanceColumns : historyColumns;

  const sectionTitle =
    activeTab === "balance"
      ? "Leave Balance Overview"
      : activeTab === "history"
        ? "Leave History"
        : "Upcoming Leaves";

  const sectionDesc =
    activeTab === "balance"
      ? "Detailed summary of your leave quotas and usage."
      : activeTab === "history"
        ? "All leave applications you have submitted."
        : "Approved or pending leaves starting from today.";

  return (
    <div className="w-full p-3 sm:p-5 lg:p-6">
      <div className="w-full space-y-6 rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_8px_24px_rgba(15,23,42,0.04)] sm:space-y-7 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3.5">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F9F0F4] text-base font-bold tracking-wide text-[#A02448] shadow-[0_0_0_4px_rgba(132,28,57,0.08)]">
            {getInitials(userName)}
          </div>
          <div>
            <h1 className="text-[22px] font-bold leading-tight tracking-tight text-[#111827]">
              {userName}
            </h1>
            <p className="mt-0.5 text-sm text-[#6B7280]">
              Manage your leave and track your balance.
            </p>
          </div>
        </div>

        <div className="relative flex items-center gap-2 self-end sm:self-auto">
          {canCreate ? (
            <YhButton
              variant="primary"
              size="md"
              startIcon={<PlusIcon />}
              onClick={openApplyLeave}
              className="!bg-[#A02448] hover:!bg-[#6B1630]"
            >
              Apply Leave
            </YhButton>
          ) : null}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
        {summaryCards.map((card) => {
          const pct = pctAvailable(card.available, card.entitled);
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => setActiveTab("balance")}
              className="group rounded-2xl border border-[#EEF0F5] bg-[#FAFBFC] p-5 text-left shadow-[0_4px_16px_rgba(15,23,42,0.03)] transition hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_12px_28px_rgba(15,23,42,0.08)] sm:p-6"
            >
              <div className="mb-5 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{
                      backgroundColor: card.accentSoft,
                      color: card.accent,
                    }}
                  >
                    {card.icon}
                  </div>
                  <div className="text-[15px] font-semibold text-[#374151]">
                    {card.type}
                  </div>
                </div>
                <ChevronIcon className="mt-1 text-[#D1D5DB] transition group-hover:text-[#9CA3AF]" />
              </div>

              <div className="mb-3 flex items-baseline gap-1.5">
                <span
                  className="text-[28px] font-bold leading-none"
                  style={{ color: card.accent }}
                >
                  {card.available}
                </span>
                <span className="text-base font-semibold text-[#9CA3AF]">
                  / {card.entitled}
                </span>
              </div>

              <div className="mb-2.5 h-1.5 overflow-hidden rounded-full bg-[#F3F4F6]">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: card.accent,
                  }}
                />
              </div>
              <div
                className="text-xs font-semibold"
                style={{ color: card.accent }}
              >
                {pct}% Available
              </div>
            </button>
          );
        })}
      </div>

      {/* Tabs + table + footer */}
      <div className="overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white">
        <div className="flex gap-1 overflow-x-auto border-b border-[#EEF0F5] px-4 sm:px-6">
          {tabs.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative inline-flex items-center gap-2 whitespace-nowrap px-3 py-4 text-sm font-semibold transition ${
                  active
                    ? "text-[#A02448]"
                    : "text-[#6B7280] hover:text-[#374151]"
                }`}
              >
                {tab.icon}
                {tab.label}
                {active ? (
                  <span className="absolute inset-x-1 bottom-0 h-[3px] rounded-t-full bg-[#A02448]" />
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-6">
          <div>
            <h2 className="text-lg font-bold text-[#111827]">{sectionTitle}</h2>
            <p className="mt-0.5 text-sm text-[#6B7280]">{sectionDesc}</p>
          </div>
          <div className="w-full sm:w-[230px]">
            <YhSelect
              options={yearOptions}
              value={calendarYear}
              onChange={(e) => setCalendarYear(String(e.target.value))}
            />
          </div>
        </div>

        <div className="px-2 pb-3 sm:px-4">
          <YhTable
            columns={tableColumns}
            rows={tableRows}
            getRowId={(row) => row.id || row._id}
            loading={activeTab === "balance" ? loadingBalance : loadingHistory}
            emptyMessage={
              activeTab === "balance"
                ? "No leave balance found"
                : activeTab === "history"
                  ? "No leave history found"
                  : "No upcoming leaves"
            }
            rowHeight={activeTab === "balance" ? 80 : 64}
            showPagination={activeTab !== "balance"}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            totalRows={tableRows.length}
            onPageChange={(p) =>
              setPaginationModel((prev) => ({ ...prev, page: p }))
            }
            onPageSizeChange={(s) =>
              setPaginationModel({ pageSize: s, page: 0 })
            }
            className="!min-h-0 !rounded-none !border-0 !shadow-none"
          />
        </div>

        <div className="mx-4 mb-5 flex items-center gap-3 rounded-xl bg-[#F5F3FF] px-4 py-3.5 sm:mx-6 sm:mb-6 sm:px-5 sm:py-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#A02448] shadow-sm">
            <InfoIcon />
          </div>
          <p className="text-sm text-[#5B21B6]">
            <span className="font-semibold">Need more leaves?</span>{" "}
            Contact your manager or HR to request additional leave.
            {canCreate ? (
              <>
                {" "}
                <button
                  type="button"
                  onClick={openApplyLeave}
                  className="font-semibold underline underline-offset-2 hover:text-[#A02448]"
                >
                  Request Leave
                </button>
              </>
            ) : null}
          </p>
        </div>
      </div>
      </div>

      {isOpenLeaveForm && (
        <NewLeaveApplication
          isDialogOpen={true}
          leaveInfo={leaveInfo}
          handleCloseLeavedetailsForm={handleCloseLeaveForm}
        />
      )}
    </div>
  );
};

export default Leave;
